import { ViewDay, ViewList } from "@mui/icons-material";
import {
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { ProcessExecutionAPI } from "../../../api";
import ContentEditable from "../../../components/ContentEditable";
import { rightPanelAtom } from "../../../components/layout/dashboard/RightPanel";
import { PageHeader } from "../../../components/layout/page";
import PATHS from "../../../components/navigation/_paths";
import { useDisplayOptions } from "../../../hooks/useDisplayOptions";
import useFeature from "../../../hooks/useFeature";
import { useGlobalOrganizationContext } from "../../../hooks/useGlobalOrganizationContext";
import { useGlobalUserContext } from "../../../hooks/useGlobalUserContext";
import { useAlert } from "../../../lib/alert";
import {
  createWebSocketMessage,
  WebSocketGlobalState,
} from "../../../lib/websocket/websocket";
import { ProcessStructure } from "../../../model/Process";
import {
  WebSocketMessageType,
  WebSocketTrackingListen,
} from "../../../model/WebSocketMessage";
import NotFound from "../../app/NotFound";
import { TaskExecution, TaskExecutionState } from "../../task";
import { ProcessExecutionProvider } from "../hooks";
import useProcessExecution, {
  ProcessExecutionViewMode,
  processExecutionViewModeAtom,
  ProcessExecutionViewState,
} from "../hooks/useProcessExecution";
import {
  ProcessAnimationProvider,
  useProcessAnimationContext,
} from "./AnimationContext";
import { ProcessExecutionScreen } from "./execution";
import ProcessExecutionCompletionAnimation from "./ProcessExecutionCompletionAnimation";
import { ProcessExecutionSidePanel } from "./ProcessExecutionSidePanel";
import VerticalSteppedProcessExecution from "./VerticalSteppedProcessExecution";

const ProcessExecutionView = ({
  processExecutionId,
}: {
  processExecutionId: string;
}) => {
  const toggleProcessViewMode = useFeature("process.viewMode");

  const { error } = useAlert();
  const { accountListIsMemberOf, isMember } = useGlobalOrganizationContext();
  const processExecutionValues = useProcessExecution(processExecutionId);
  const {
    processExecution,
    process,
    taskExecutions,
    steps,
    stepExecutions,
    assignedRoleUsers,
    viewState,
  } = processExecutionValues;
  const anim = useProcessAnimationContext();
  const [viewMode, setViewMode] = useAtom(processExecutionViewModeAtom);
  const [, setRightPanel] = useAtom(rightPanelAtom);
  const [attachmentReloadTrigger, setAttachmentReloadTrigger] = useState(0);
  const [completionOpen, setCompletionOpen] = useState(false);
  const user = useGlobalUserContext().user;

  const submissionMessage = useMemo(() => {
    const lastFinishedExecution = [...taskExecutions]
      .filter((ex) => !!ex)
      .reverse()
      .find((execution) => {
        return execution?.state != TaskExecutionState.InProgress;
      });
    return lastFinishedExecution?.task?.data?.submission;
  }, [taskExecutions]);

  useEffect(() => {
    if (anim?.taskJustSubmitted) {
      const nextRole = processExecution?.next_task_instance?.role;
      const userRoles = user.roles;
      const canPlayAnimation = !userRoles?.find((r) => r.id === nextRole?.id);
      if (canPlayAnimation) {
        setCompletionOpen(true);
        const timeout = setTimeout(() => setCompletionOpen(false), 5000);
        return () => clearTimeout(timeout);
      }
    }
  }, [
    anim?.taskJustSubmitted,
    processExecution?.next_task_instance?.role,
    user.roles,
  ]);

  const allTasksView = useMemo(() => {
    return (
      steps?.length > 0 || !(viewMode === ProcessExecutionViewMode.Focused)
    );
  }, [steps?.length, viewMode]);

  const account = useMemo(() => {
    if (isMember(processExecution?.org_id)) {
      return undefined;
    }
    return accountListIsMemberOf.find(
      (account) => account.org_id === processExecution?.org_id,
    );
  }, [accountListIsMemberOf, isMember, processExecution?.org_id]);

  useDisplayOptions({
    showDrawer: false,
    showTopBorder: true,
    topBorderColor: processExecution?.display?.fillColor,
  });

  useEffect(() => {
    const trackingData: WebSocketTrackingListen = {
      type: "process_execution",
      subject_id: processExecutionId,
    };

    const webSocketMessageToSend = createWebSocketMessage(
      WebSocketMessageType.TRACKING,
      trackingData,
    );

    WebSocketGlobalState.getWebSocket()?.send(
      JSON.stringify(webSocketMessageToSend),
    );
  }, [processExecutionId]);

  useEffect(() => {
    setRightPanel((prev) => ({
      ...prev,
      open: true,
      variant: "collapse",
      content: () => {
        return (
          <ProcessExecutionSidePanel
            organizationId={process?.org_id}
            processExecution={processExecution}
            attachmentReloadTrigger={attachmentReloadTrigger}
            assignedRoleUsers={assignedRoleUsers}
          />
        );
      },
    }));
    return () => {
      setRightPanel((prev) => ({
        ...prev,
        open: false,
      }));
    };
  }, [
    assignedRoleUsers,
    attachmentReloadTrigger,
    process?.org_id,
    processExecution,
    setRightPanel,
  ]);

  if (viewState === ProcessExecutionViewState.NotFound) {
    return <NotFound />;
  }

  return (
    <ProcessExecutionProvider value={processExecutionValues}>
      <Grid
        container
        wrap="nowrap"
        direction={"column"}
        height={"100%"}
        sx={{ backgroundColor: "background.paper" }}
      >
        {/* Header */}
        <Grid container>
          <PageHeader
            title={processExecution?.name}
            variant="primary"
            color={processExecution?.display?.fillColor}
            links={
              isMember(processExecution?.org_id)
                ? [PATHS.ORGANIZATION_EXECUTIONS]
                : [
                    {
                      ...PATHS.ACCOUNT_PORTAL,
                      link: PATHS.ACCOUNT_PORTAL.linkTo(account?.id ?? ""),
                    },
                  ]
            }
          >
            <Typography
              variant="h1"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {processExecution?.name && (
                <ContentEditable
                  value={processExecution?.name}
                  onChange={async (newValue: string) => {
                    const updatedExecution = { ...processExecution };
                    updatedExecution.name = newValue;
                    try {
                      await ProcessExecutionAPI.update(
                        updatedExecution.id,
                        updatedExecution,
                      );
                      return true;
                    } catch (e) {
                      console.error(e);
                      error("Failed to update process execution name");
                      return false;
                    }
                  }}
                />
              )}
            </Typography>
          </PageHeader>
        </Grid>

        {/* Content */}
        <Grid
          minWidth={"50%"}
          container
          flexDirection={"column"}
          overflow={"hidden"}
          height={"100%"}
          wrap="nowrap"
          sx={{ background: "#F4F7F9", position: "relative" }}
        >
          {/* Main content */}
          <Grid container wrap="nowrap" overflow={"auto"}>
            {processExecution && process && (
              <>
                {allTasksView && (
                  <Grid
                    sx={{ width: "60%", minWidth: "480px", margin: "0 auto" }}
                  >
                    <VerticalSteppedProcessExecution
                      steps={steps}
                      process={process}
                      processExecution={processExecution}
                      stepExecutions={stepExecutions}
                      taskExecutions={taskExecutions.filter(
                        (te): te is TaskExecution => !!te,
                      )}
                      assignedRoles={assignedRoleUsers}
                      onShare={() => {
                        setAttachmentReloadTrigger((trigger) => trigger + 1);
                      }}
                    />
                  </Grid>
                )}
                {!allTasksView && (
                  <Grid flex={1}>
                    <ProcessExecutionScreen />
                  </Grid>
                )}
              </>
            )}
            {toggleProcessViewMode &&
            process?.structure == ProcessStructure.Flat ? (
              <Grid>
                <ToggleButtonGroup
                  sx={{ m: 2 }}
                  size="small"
                  value={viewMode}
                  color="primary"
                  exclusive
                  onChange={(e, view) => setViewMode(view)}
                >
                  <ToggleButton value={ProcessExecutionViewMode.List}>
                    <Tooltip title="List">
                      <ViewList />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value={ProcessExecutionViewMode.Focused}>
                    <Tooltip title="Focused">
                      <ViewDay />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            ) : (
              <Grid sx={{ m: 4 }}></Grid>
            )}
          </Grid>

          {/* Overlay Box */}
          <ProcessExecutionCompletionAnimation
            open={completionOpen}
            onClose={() => {
              setCompletionOpen(false);
            }}
            title={submissionMessage?.title}
            message={submissionMessage?.message}
          />
        </Grid>
      </Grid>
    </ProcessExecutionProvider>
  );
};

const ProcessExecutionViewPage = () => {
  const { processExecutionId } = useParams<{ processExecutionId: string }>();
  if (!processExecutionId) {
    return <NotFound />;
  }
  return (
    <ProcessAnimationProvider>
      <ProcessExecutionView processExecutionId={processExecutionId} />
    </ProcessAnimationProvider>
  );
};

export default ProcessExecutionViewPage;
